import {
	type AnyPipelineMessageStageProcessor,
	type AnyPipelineMessageStageProcessorAsyncExecute,
} from '@post-office/shared-contracts/pipeline';
import type { PipelineMessage } from '@post-office/shared-contracts/types';

import { type RuntimeParams } from '../../create-placement-component/types';
import { PlacementSystemError } from '../../error';
import {
	type AnyBackendMessageBody,
	type AnyBackendPlacementStage,
	type AnyPayload,
	type PlacementBackendRuntime,
	type PlacementStages,
	type RequestContext,
} from '../../types';
import { createPlacementStages } from '../../util/runtime';

export const productionRuntime: PlacementBackendRuntime = (config) => {
	const placementStages = createPlacementStages(config);

	const stageProcessors = placementStagesToStageProcessors(placementStages);
	return ({ koa, request }) => {
		const enableBypassBuffer = request.statsig.checkGate(
			{
				identifiers: {
					atlassianAccountId: request?.orchestrationContext?.recipient?.toString(),
				},
			},
			'post-office_enable_bypass-data',
		);

		// This is stateful mechanism to allow placement stages to send none message data between them.
		// Examples include grouping data, or other metadata provided by the placement.
		// Orchestration messageSelection only accepts PipelineMessage[] as an input, which means we can't add it to the pipeline payload. >(ভ⤙ ভ ")<
		// Be aware that because this is stateful it is dangerous. Be sure that this only is instantiated per request to prevent metadata
		// cross contamination between requests
		const bypassBuffer = createBypassBuffer();

		const stages = stageProcessors({ request, bypassBuffer, enableBypassBuffer });

		const orchestrationRequest = request.location.tag({ placementLevel: 'PostOffice' });

		return async (params: RuntimeParams) => {
			const messages = await koa.ctx.messageSelection.getEligibleMessages(orchestrationRequest, {
				...params,
				stages,
			});

			if (enableBypassBuffer) {
				const bypassData = bypassBuffer.getOnce();

				return {
					data: { ...bypassData, messages },
					// Add error output when compatible with orchestration
					errors: [],
				};
			}

			return {
				data: { messages },
				// Add error output when compatible with orchestration
				errors: [],
			};
		};
	};
};

const placementStagesToStageProcessors =
	(placementStages: PlacementStages) =>
	(contexts: {
		request: RequestContext;
		bypassBuffer: BypassBuffer;
		enableBypassBuffer: boolean;
	}): { [stageName in keyof PlacementStages]: AnyPipelineMessageStageProcessor } => {
		const stageProcessors: {
			[stageName in keyof PlacementStages]: AnyPipelineMessageStageProcessor;
		} = {};

		Object.entries(placementStages).forEach((stage) => {
			const [stageName] = stage;

			stageProcessors[stageName as keyof PlacementStages] =
				placementStageToStageProcessor(contexts)(stage);
		});

		return stageProcessors;
	};

const placementStageToStageProcessor =
	(contexts: {
		request: RequestContext;
		bypassBuffer: BypassBuffer;
		enableBypassBuffer: boolean;
	}) =>
	([stageName, placementStage]: [
		stageName: string,
		placementStage: AnyBackendPlacementStage | undefined,
	]): AnyPipelineMessageStageProcessor => {
		const execute = placementStageToStageProcessorExecute(placementStage)(contexts);

		return () => ({
			id: stageName,
			execute,
		});
	};

type BypassBuffer<T extends AnyPayload = AnyPayload> = {
	set: (buffer: T) => void;
	getOnce: () => T | undefined;
	flush: () => void;
};

const createBypassBuffer = <T extends AnyPayload = AnyPayload>(): BypassBuffer<T> => {
	let state: T | undefined = undefined;

	const getOnce: BypassBuffer<T>['getOnce'] = () => {
		const once = state;
		flush();

		return once;
	};

	const set: BypassBuffer<T>['set'] = (buffer) => {
		state = buffer;
	};

	const flush: BypassBuffer<T>['flush'] = () => {
		state = undefined;
	};

	return {
		getOnce,
		set,
		flush,
	};
};

const placementStageToStageProcessorExecute =
	(placementStage: AnyBackendPlacementStage = identityPlacementStage) =>
	({
		request,
		bypassBuffer,
		enableBypassBuffer,
	}: {
		request: RequestContext;
		bypassBuffer: BypassBuffer;
		enableBypassBuffer: boolean;
	}): AnyPipelineMessageStageProcessorAsyncExecute =>
	async (pipelineMessages) => {
		const placementMessages = pipelineMessages.map(stripUnserializablePayload);

		const pipelineMessagesByMessageInstanceId: Record<string, PipelineMessage> = Object.fromEntries(
			pipelineMessages.map((pipelineMessage) => [
				pipelineMessage.messageInstanceId,
				pipelineMessage,
			]),
		);

		validateSerializablePayload(placementMessages);

		if (enableBypassBuffer) {
			const bypassInput = bypassBuffer.getOnce();

			const processedData = await placementStage({
				data: { ...bypassInput, messages: placementMessages },
				request,
			});

			const { messages: _, ...bypassOutput } = processedData;

			bypassBuffer.set(bypassOutput);

			const combinedMessages = processedData.messages.map(
				(processedMessage) =>
					({
						...pipelineMessagesByMessageInstanceId[processedMessage.messageInstanceId],
						...processedMessage,
					}) as PipelineMessage,
			);

			return combinedMessages;
		}

		const processedData = await placementStage({
			data: { messages: placementMessages },
			request,
		});

		const combinedMessages = processedData.messages.map(
			(processedMessage) =>
				({
					...pipelineMessagesByMessageInstanceId[processedMessage.messageInstanceId],
					...processedMessage,
				}) as PipelineMessage,
		);

		return combinedMessages;
	};

const stripUnserializablePayload = (pipelineMessage: PipelineMessage): AnyBackendMessageBody => ({
	messageInstanceId: pipelineMessage.messageInstanceId,
	messageTemplateId: pipelineMessage.messageTemplateId,
	messageCategory: pipelineMessage.messageCategory,
	createdAt: pipelineMessage.createdAt,
	eventTime: pipelineMessage.eventTime,
	context: pipelineMessage.context,
	triggerId: pipelineMessage.triggerId,
	messageCreationType: pipelineMessage.messageCreationType,
	recommendationSession: pipelineMessage.recommendationSession,
	readStatus: pipelineMessage.readStatus,
});

const validateSerializablePayload = (payload: unknown): void => {
	try {
		JSON.stringify(payload);
	} catch {
		throw new PlacementSystemError({
			message:
				'failed to backend message body, this may mean a non-serializable field has to been added and should be removed',
		});
	}
};

const identityPlacementStage: AnyBackendPlacementStage = async (input) => input.data;
